import React, { useMemo, useState } from 'react'
import clsx from 'clsx'
import {
  ArrowPathIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, Select, Text } from '../../ui'
import { usePermission } from '../../../hooks/usePermissions'
import { SocialPost } from '../../../types/social'
import MentionText from './MentionText'
import PostDetailModal from './PostDetailModal'
import PostImages from './PostImages'
import { normalizeReactionEmoji, ReactionEmojiIcon } from './ReactionEmoji'

export interface PostListProps {
  posts: SocialPost[]
  loading: boolean
  error: string | null
  refreshing: boolean
  onRefresh: () => void
  onDelete: (postId: string) => void | Promise<void>
  onDeleteComment: (commentId: string) => void | Promise<void>
}

type CommentsFilter = 'all' | 'with' | 'without'
type ReactionsFilter = 'all' | 'with' | 'without'

interface AppliedFilters {
  author: string
  content: string
  dateFrom: string
  dateTo: string
  comments: CommentsFilter
  reactions: ReactionsFilter
}

const emptyFilters: AppliedFilters = {
  author: '',
  content: '',
  dateFrom: '',
  dateTo: '',
  comments: 'all',
  reactions: 'all',
}

const formatListDate = (dateString: string) =>
  new Date(dateString).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

const truncateContent = (content: string, maxLength = 120) => {
  const singleLine = content.replace(/\s+/g, ' ').trim()
  if (singleLine.length <= maxLength) return singleLine
  return `${singleLine.slice(0, maxLength)}…`
}

function isDateInRange(isoDate: string, from: string, to: string): boolean {
  const date = new Date(isoDate)
  if (from) {
    const [y, m, d] = from.split('-').map((p) => parseInt(p, 10))
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      const start = new Date(y, m - 1, d, 0, 0, 0, 0)
      if (date < start) return false
    }
  }
  if (to) {
    const [y, m, d] = to.split('-').map((p) => parseInt(p, 10))
    if (!Number.isNaN(y) && !Number.isNaN(m) && !Number.isNaN(d)) {
      const end = new Date(y, m - 1, d, 23, 59, 59, 999)
      if (date > end) return false
    }
  }
  return true
}

function filterPosts(posts: SocialPost[], filters: AppliedFilters): SocialPost[] {
  const authorQuery = filters.author.trim().toLowerCase()
  const contentQuery = filters.content.trim().toLowerCase()

  return posts.filter((post) => {
    const authorName = (post.author?.name || 'Usuário').toLowerCase()
    if (authorQuery && !authorName.includes(authorQuery)) return false

    if (contentQuery && !post.content.toLowerCase().includes(contentQuery)) return false

    if (!isDateInRange(post.created_at, filters.dateFrom, filters.dateTo)) return false

    const commentCount = post.comments?.length ?? post.comment_count ?? 0
    if (filters.comments === 'with' && commentCount === 0) return false
    if (filters.comments === 'without' && commentCount > 0) return false

    const reactionCount = (post.reactions || []).length
    if (filters.reactions === 'with' && reactionCount === 0) return false
    if (filters.reactions === 'without' && reactionCount > 0) return false

    return true
  })
}

function hasActiveFilters(filters: AppliedFilters): boolean {
  return (
    filters.author.trim() !== '' ||
    filters.content.trim() !== '' ||
    filters.dateFrom !== '' ||
    filters.dateTo !== '' ||
    filters.comments !== 'all' ||
    filters.reactions !== 'all'
  )
}

const PostList: React.FC<PostListProps> = ({
  posts,
  loading,
  error,
  refreshing,
  onRefresh,
  onDelete,
  onDeleteComment,
}) => {
  const { hasPermission: canDelete } = usePermission('social_posts', 'delete')
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [selectedPostId, setSelectedPostId] = useState<string | null>(null)

  const [authorFilter, setAuthorFilter] = useState('')
  const [contentFilter, setContentFilter] = useState('')
  const [dateFromFilter, setDateFromFilter] = useState('')
  const [dateToFilter, setDateToFilter] = useState('')
  const [commentsFilter, setCommentsFilter] = useState<CommentsFilter>('all')
  const [reactionsFilter, setReactionsFilter] = useState<ReactionsFilter>('all')
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>(emptyFilters)
  const [hasSearched, setHasSearched] = useState(false)

  const filteredPosts = useMemo(() => {
    if (!hasSearched) return posts
    return filterPosts(posts, appliedFilters)
  }, [posts, appliedFilters, hasSearched])

  const selectedPost = selectedPostId
    ? filteredPosts.find((p) => p.id === selectedPostId)
      ?? posts.find((p) => p.id === selectedPostId)
      ?? null
    : null

  const draftHasFilters =
    authorFilter.trim() !== '' ||
    contentFilter.trim() !== '' ||
    dateFromFilter !== '' ||
    dateToFilter !== '' ||
    commentsFilter !== 'all' ||
    reactionsFilter !== 'all'

  const handleBuscar = () => {
    setAppliedFilters({
      author: authorFilter,
      content: contentFilter,
      dateFrom: dateFromFilter,
      dateTo: dateToFilter,
      comments: commentsFilter,
      reactions: reactionsFilter,
    })
    setHasSearched(true)
  }

  const handleLimparFiltros = () => {
    setAuthorFilter('')
    setContentFilter('')
    setDateFromFilter('')
    setDateToFilter('')
    setCommentsFilter('all')
    setReactionsFilter('all')
    setAppliedFilters(emptyFilters)
    setHasSearched(false)
  }

  const handleDelete = async (postId: string) => {
    if (!canDelete) {
      setActionError('Você não tem permissão para excluir postagens.')
      return
    }
    if (!window.confirm('Excluir esta postagem?')) return
    try {
      setDeletingId(postId)
      setActionError(null)
      await onDelete(postId)
      if (selectedPostId === postId) setSelectedPostId(null)
    } catch (err) {
      setActionError(
        err instanceof Error
          ? err.message
          : err && typeof err === 'object' && 'message' in err && typeof (err as { message: unknown }).message === 'string'
            ? (err as { message: string }).message
            : 'Erro ao excluir post'
      )
    } finally {
      setDeletingId(null)
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <FunnelIcon className="h-5 w-5 text-gray-500 dark:text-gray-400 mr-2" />
          <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
            Filtros
          </Heading>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Autor
            </label>
            <Input
              type="text"
              placeholder="Nome do autor..."
              value={authorFilter}
              onChange={(e) => setAuthorFilter(e.target.value)}
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Buscar na publicação
            </label>
            <div className="relative">
              <Input
                type="text"
                placeholder="Digite para buscar..."
                value={contentFilter}
                onChange={(e) => setContentFilter(e.target.value)}
                className="pl-10"
                disabled={loading}
              />
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Comentários
            </label>
            <Select
              value={commentsFilter}
              onChange={(e) => setCommentsFilter(e.target.value as CommentsFilter)}
              disabled={loading}
            >
              <option value="all">Todos</option>
              <option value="with">Com comentários</option>
              <option value="without">Sem comentários</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Reações
            </label>
            <Select
              value={reactionsFilter}
              onChange={(e) => setReactionsFilter(e.target.value as ReactionsFilter)}
              disabled={loading}
            >
              <option value="all">Todos</option>
              <option value="with">Com reações</option>
              <option value="without">Sem reações</option>
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Data inicial
            </label>
            <Input
              type="date"
              value={dateFromFilter}
              onChange={(e) => setDateFromFilter(e.target.value)}
              disabled={loading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Data final
            </label>
            <Input
              type="date"
              value={dateToFilter}
              onChange={(e) => setDateToFilter(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            variant="solid"
            size="sm"
            onClick={handleBuscar}
            disabled={loading || !draftHasFilters}
            title={!draftHasFilters ? 'Selecione ao menos um filtro para buscar' : undefined}
          >
            {loading ? 'Buscando...' : 'Buscar'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLimparFiltros}
            disabled={loading || (!hasSearched && !draftHasFilters)}
          >
            <XMarkIcon className="h-4 w-4 mr-2 inline" />
            Limpar Filtros
          </Button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
        <Text size="sm" className="text-gray-600 dark:text-gray-400">
          {loading
            ? 'Carregando…'
            : hasSearched && hasActiveFilters(appliedFilters)
              ? `${filteredPosts.length} de ${posts.length} ${posts.length === 1 ? 'postagem' : 'postagens'}`
              : `${posts.length} ${posts.length === 1 ? 'postagem' : 'postagens'}`}
        </Text>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing || loading}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 dark:border-gray-600 px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50 transition-colors"
        >
          <ArrowPathIcon className={clsx('h-4 w-4', refreshing && 'animate-spin')} />
          Atualizar
        </button>
      </div>

      {actionError && (
        <div className="px-6 py-3 border-b border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30">
          <Text size="sm" className="text-red-700 dark:text-red-300">{actionError}</Text>
        </div>
      )}

      {loading ? (
        <div className="px-6 py-10 flex justify-center">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="px-6 py-10 text-center">
          <Text size="sm" className="text-red-600 dark:text-red-400">{error}</Text>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="px-6 py-10 text-center">
          <Text size="sm" color="muted">
            {hasSearched && hasActiveFilters(appliedFilters)
              ? 'Nenhuma postagem encontrada com os filtros aplicados.'
              : 'Nenhuma postagem no feed.'}
          </Text>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Autor
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Publicação
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Data
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Reações
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider whitespace-nowrap">
                  Comentários
                </th>
                {canDelete && (
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-24">
                    Ações
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredPosts.map((post) => {
                const reactions = post.reactions || []
                const commentCount = post.comments?.length ?? post.comment_count ?? 0
                const uniqueReactionEmojis =
                  reactions.length > 0
                    ? Array.from(new Set(reactions.map((r) => normalizeReactionEmoji(r.emoji))))
                    : []

                return (
                  <tr
                    key={post.id}
                    onClick={() => setSelectedPostId(post.id)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                      {post.author?.name || 'Usuário'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 max-w-md">
                      <div className="flex gap-2 items-start min-w-0">
                        {(post.images?.length ?? 0) > 0 && (
                          <PostImages images={post.images} compact />
                        )}
                        <div className="min-w-0 flex-1">
                          {post.content.trim() ? (
                            <span className="line-clamp-2" title={post.content}>
                              <MentionText content={truncateContent(post.content)} />
                            </span>
                          ) : (post.images?.length ?? 0) > 0 ? (
                            <Text size="sm" color="muted">Imagem</Text>
                          ) : (
                            <span className="line-clamp-2">—</span>
                          )}
                          {post.edited_at && (
                            <Text size="xs" color="muted" className="mt-0.5">
                              Editado
                            </Text>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {formatListDate(post.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {uniqueReactionEmojis.length > 0 ? (
                        <span className="inline-flex items-center justify-center gap-1">
                          {uniqueReactionEmojis.map((emoji) => (
                            <ReactionEmojiIcon key={emoji} emoji={emoji} size="sm" />
                          ))}
                          <span className="ml-0.5 text-gray-400 dark:text-gray-500">
                            ({reactions.length})
                          </span>
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {commentCount}
                    </td>
                    {canDelete && (
                      <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => void handleDelete(post.id)}
                          disabled={deletingId === post.id}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
                          title="Excluir postagem"
                        >
                          <TrashIcon className="h-4 w-4" />
                          Excluir
                        </button>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedPost && (
        <PostDetailModal
          post={selectedPost}
          onClose={() => setSelectedPostId(null)}
          onDeleteComment={onDeleteComment}
          canDeleteComments={canDelete}
        />
      )}
      </div>
    </div>
  )
}

export default PostList
